import React, { useContext } from 'react'
import { PageHeading, Heading, Markdown, Link } from '../shared/typography'
import {
  Switch,
  Route,
  Link as RouterLink,
  useLocation,
  Redirect,
} from 'react-router-dom'
import dig from 'object-dig'
import Sidebar, { PrimaryNavLink, SecondaryNavLink } from '../layout/sidebar'
import ContentWrapper from '../layout/content_wrapper'
import Loading from '../shared/loading'
import ErrorUI from '../shared/error_ui'
import store from '../../api/store'
import request from '../../api/request'
import Vertex from './vertex'
import Apps from '../../api/apps'
import VersionDetails from './version'
import VisibilitySensor from 'react-visibility-sensor'
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed'
import SourceSwitcher from './source_switcher'
import { BetaBadge, DeprecatedBadge } from '../shared/badges'
import {
  Alert,
  StackView,
  Icon,
  Text,
  Box,
  Button,
} from '@planningcenter/tapestry-react'
import VersionSwitcher from './version_switcher'
import { MenuContext } from '../layout/global_layout'
import { breakpoints } from '../theme'
import useWindowWidth from '../utils/useWindowWidth'

const App = ({
  name,
  path,
  isLoading,
  current,
  version,
  versions,
  isBeta,
  vertexId,
  onVersionChange,
}) => {
  const { setMobileMenuOpen } = useContext(MenuContext)
  const windowWidth = useWindowWidth()
  const location = useLocation()
  const { pathname } = location

  const closeMobileMenu = () => {
    if (windowWidth < breakpoints.lg) {
      setMobileMenuOpen(false)
    } else {
      return false
    }
  }
  return (
    <>
      <PrimaryNavLink
        to={path}
        selected={pathname.includes(path.toLowerCase())}
      >
        {isBeta && <BetaBadge marginLeft={1} />}
        {name}
      </PrimaryNavLink>

      <Loading isLoading={isLoading && current}>
        {current ? (
          <>
            <VersionSwitcher
              onVersionChange={onVersionChange}
              version={version}
              versions={versions}
            />
            {versions.map(vers => {
              const isActive = vers.id === version
              if (!isActive) {
                return null
              }
              return (
                <div key={vers.id}>
                  {vers.relationships.vertices.data.map(vertex => {
                    const vertexActive = vertex.id === vertexId

                    return (
                      <VisibilitySensor>
                        <ScrollIntoViewIfNeeded
                          options={{
                            behavior: 'instant',
                            scrollMode: 'if-needed',
                          }}
                          active={vertexActive}
                        >
                          <SecondaryNavLink
                            key={vertex.id}
                            onClick={() => closeMobileMenu()}
                            selected={vertexActive}
                            to={`${path}/${vers.id}/vertices/${vertex.id}`}
                          >
                            {vertex.attributes.name}
                            {vertex.attributes.deprecated && (
                              <DeprecatedBadge size="xs" marginLeft={1} />
                            )}
                          </SecondaryNavLink>
                        </ScrollIntoViewIfNeeded>
                      </VisibilitySensor>
                    )
                  })}
                </div>
              )
            })}
          </>
        ) : null}
      </Loading>
    </>
  )
}

const Version = ({ appName, id, details, beta, path, links }) => {
  return (
    <Box>
      <StackView axis="horizontal" alignment="center">
        <RouterLink to={`/apps${path}/${id}`}>
          <Heading color="primary">{id}</Heading>
        </RouterLink>
        {beta && <BetaBadge marginLeft={1} />}
      </StackView>

      <Markdown>{details}</Markdown>

      {links?.open_api && (
        <Button
          download={`${appName}_${id}.json`}
          fontWeight="normal"
          iconLeft={{ name: 'general.genericFile' }}
          marginTop={1.5}
          onClick={Version.downloadOpenAPIDescription}
          role="button"
          size="sm"
          title="OpenAPI Description"
          to={links.open_api}
        />
      )}
    </Box>
  )
}
Version.downloadOpenAPIDescription = async function (event) {
  event.preventDefault()

  const link = event.currentTarget
  const filename = link.getAttribute('download') || 'open_api.json'

  try {
    const response = await fetch(link.href)

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    })
    const blobUrl = URL.createObjectURL(blob)

    let a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    URL.revokeObjectURL(blobUrl)
  } catch (e) {
    console.error(e)
    alert('Unable to download the OpenAPI description.')
  }
}

const Graph = ({ title, description, versions, path, appName }) => {
  return (
    <StackView axis="vertical" spacing={3}>
      <PageHeading>{title}</PageHeading>
      <Markdown>{description}</Markdown>

      {appName === 'calendar' && (
        <Alert type="warning" padding={2} marginBottom={2}>
          <StackView axis="horizontal" alignment="center" spacing={2}>
            <Icon
              name="general.exclamationCircle"
              size="xl"
              color="warning-dark"
            />
            <Text size={4}>
              Calendar was previously called Resources and while we'll continue
              to support existing Resources connections, we will only be issuing
              OAUTH tokens for Calendar going forward.
            </Text>
          </StackView>
        </Alert>
      )}

      {appName === 'giving' && (
        <Alert type="success" padding={2} marginBottom={2}>
          <StackView axis="horizontal" alignment="center" spacing={2}>
            <Text size={4}>
              Looking to build a Giving Integration? Be sure to check out our{' '}
              <Link
                to="https://github.com/planningcenter/developers/blob/main/guides/giving_donations_integration.md"
                external
              >
                Integration Guide
              </Link>
              .
            </Text>
          </StackView>
        </Alert>
      )}

      <Heading style={{ marginBottom: 4 }}>Available Versions</Heading>
      {versions.map(version => {
        return (
          <Version
            appName={appName}
            key={version.id}
            path={path}
            id={version.id}
            entryId={version.relationships.entry.data.id}
            links={version.links}
            {...version.attributes}
          />
        )
      })}
    </StackView>
  )
}

const LatestVersionRedirect = ({ versions }) => {
  const location = useLocation()
  const [appPart, ...rest] = location.pathname.split(/\/latest(?:\/|$)/)
  const redirect = [appPart, versions[0].id, ...rest.filter(Boolean)].join('/')

  return <Redirect to={redirect} />
}

export default class Documentation extends React.Component {
  state = {
    isLoading: true,
    graph: null,
    error: null,
  }

  componentDidMount() {
    this.fetchGraph()
  }

  UNSAFE_componentWillReceiveProps(next) {
    if (next.match.params.appName !== this.props.match.params.appName) {
      this.setState({ graph: null }, this.fetchGraph)
    }
  }

  _navigateToVersion = version => {
    const { appName } = this.props.match.params
    if (version === 'none') {
      this.props.history.push(`/apps/${appName}`)
    } else {
      this.props.history.push(`/apps/${appName}/${version}`)
    }
  }

  _versions = () => {
    const versions =
      dig(this.state, 'graph', 'relationships', 'versions', 'data') || []
    return versions
  }

  fetchGraph = () => {
    this.setState({ isLoading: true, error: null }, () => {
      const { appName } = this.props.match.params
      request(`/${appName}/v2/documentation`)
        .then(response => {
          if (this.props.match.params.appName !== appName) {
            throw new Error('EARLY EXIT')
          }
          return response
        })
        .then(response => {
          this.setState({ graph: response.data.data })
        })
        .then(() => {
          store.set('lastApp', appName)
        })
        .catch(error => {
          if (error.message !== 'EARLY EXIT') {
            this.setState({ error })
          }
        })
        .finally(() => {
          this.setState({ isLoading: false })
        })
    })
  }

  render() {
    const { isLoading, graph, error } = this.state
    const { appName, version, vertexId } = this.props.match.params
    const versions = dig(graph, 'relationships', 'versions', 'data')

    return (
      <>
        <Sidebar>
          <>
            <SourceSwitcher />
            {Apps.map(app => {
              const { name, key, beta } = app
              return (
                <App
                  key={key}
                  current={appName === key}
                  name={name}
                  path={`/apps/${key}`}
                  isLoading={isLoading && appName === key}
                  versions={this._versions()}
                  version={version}
                  vertexId={vertexId}
                  isBeta={beta}
                  onVersionChange={this._navigateToVersion}
                />
              )
            })}
          </>
        </Sidebar>

        <ContentWrapper>
          <ErrorUI error={error} onRetry={this.fetchGraph}>
            <Loading isLoading={isLoading} size="xxl">
              <Switch>
                <Route
                  path="/apps/:appName/latest"
                  render={() => <LatestVersionRedirect versions={versions} />}
                />
                <Route
                  path="/apps/:appName/:version/vertices/:id"
                  component={Vertex}
                />
                <Route
                  path="/apps/:appName/:version"
                  component={VersionDetails}
                />
                <Route
                  path="/"
                  render={() => {
                    if (versions) {
                      return (
                        <Graph
                          appName={appName}
                          path={`/${appName}`}
                          versions={versions}
                          {...graph.attributes}
                        />
                      )
                    } else {
                      return (
                        <StackView axis="vertical" spacing={2}>
                          <Heading size={1} color="primary">
                            We're working on getting versions rolled out.
                          </Heading>
                          <Text weight={600}>Please check back soon</Text>
                        </StackView>
                      )
                    }
                  }}
                />
              </Switch>
            </Loading>
          </ErrorUI>
        </ContentWrapper>
      </>
    )
  }
}
